<template>
  <span :class="showSlow ? 'inline-flex items-center gap-1' : 'contents'">
    <button
      v-bind="$attrs"
      @click.stop="play()"
      :disabled="loading"
      class="inline-flex items-center justify-center w-8 h-8 rounded-full transition-colors"
      :class="[
        playing ? 'bg-emerald-100 text-emerald-700' :
        loading ? 'bg-gray-100 text-gray-400 cursor-wait' :
        'bg-gray-100 text-gray-500 hover:bg-emerald-50 hover:text-emerald-600'
      ]"
      :title="loading ? 'Loading...' : 'Play audio'"
    >
      <!-- Loading spinner -->
      <svg v-if="loading" class="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" class="opacity-25" />
        <path d="M4 12a8 8 0 018-8" stroke="currentColor" stroke-width="3" stroke-linecap="round" class="opacity-75" />
      </svg>
      <!-- Speaker icon -->
      <svg v-else class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
        <path d="M11.383 3.07A1 1 0 0112 4v16a1 1 0 01-1.617.786L5.586 17H3a1 1 0 01-1-1V8a1 1 0 011-1h2.586l4.797-3.93a1 1 0 011 .001z" />
        <path v-if="!playing" d="M16.228 4.462a1 1 0 011.414.063 10.97 10.97 0 010 14.95 1 1 0 01-1.476-1.35 8.97 8.97 0 000-12.25 1 1 0 01.062-1.413zM14.293 7.636a1 1 0 011.414.05 6.97 6.97 0 010 8.628 1 1 0 01-1.464-1.364 4.97 4.97 0 000-5.95 1 1 0 01.05-1.364z" />
        <path v-else d="M16.228 4.462a1 1 0 011.414.063 10.97 10.97 0 010 14.95 1 1 0 01-1.476-1.35 8.97 8.97 0 010-12.25 1 1 0 01.062-1.413z" class="animate-pulse" />
      </svg>
    </button>
    <!-- Half-speed replay: table stakes for a tonal language. Same clip, slowed. -->
    <button
      v-if="showSlow"
      @click.stop="play({ slow: true })"
      :disabled="loading"
      class="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs transition-colors bg-gray-100 text-gray-500 hover:bg-emerald-50"
      title="Play slowly"
    >&#128034;</button>
  </span>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { getStaticAudioUrl } from '../../utils/ttsCache'

// $attrs (external classes like the big listening button's `!w-16`) must land
// on the main play button, not the wrapper.
defineOptions({ inheritAttrs: false })

const props = defineProps({
  src: { type: String, default: null },
  text: { type: String, default: null },
  autoplay: { type: Boolean, default: false },
  showSlow: { type: Boolean, default: false },
})

const SLOW_RATE = 0.65

const loading = ref(false)
const playing = ref(false)
let currentAudio = null

function speakWithBrowser(text, rate = 0.85) {
  return new Promise((resolve, reject) => {
    if (!window.speechSynthesis) return reject(new Error('No speech synthesis'))
    const utterance = new SpeechSynthesisUtterance(text)
    utterance.lang = 'vi-VN'
    utterance.rate = rate
    utterance.onend = resolve
    utterance.onerror = reject
    window.speechSynthesis.speak(utterance)
  })
}

async function play({ slow = false } = {}) {
  if (loading.value || playing.value) return

  // Stop any currently playing audio
  if (currentAudio) {
    currentAudio.pause()
    currentAudio = null
  }

  const speechRate = slow ? 0.6 : 0.85

  try {
    let audioUrl

    if (props.src) {
      // Use prefetched blob URL if available, otherwise use the raw URL
      audioUrl = getStaticAudioUrl(props.src)
    } else if (props.text) {
      // No static src — fall back to browser speech synthesis
      playing.value = true
      await speakWithBrowser(props.text, speechRate)
      playing.value = false
      return
    } else {
      return
    }

    playing.value = true

    currentAudio = new Audio(audioUrl)
    if (slow) {
      currentAudio.playbackRate = SLOW_RATE
      // Keep the pitch (and so the tones) intact while slowed. Modern browsers
      // default to pitch-preserving time-stretch; set both spellings anyway
      // (older Safari only knows the webkit- one).
      currentAudio.preservesPitch = true
      currentAudio.webkitPreservesPitch = true
    }
    currentAudio.addEventListener('ended', () => {
      playing.value = false
    })
    currentAudio.addEventListener('error', async () => {
      // If static file fails to load, fall back to browser speech
      playing.value = false
      if (props.text) {
        playing.value = true
        try { await speakWithBrowser(props.text, speechRate) } catch {}
        playing.value = false
      }
    })
    await currentAudio.play()
  } catch (e) {
    loading.value = false
    playing.value = false
  }
}

defineExpose({ play })

onMounted(() => {
  if (props.autoplay) {
    setTimeout(() => play(), 200)
  }
})
</script>
